import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MultiSelect } from '~/components/multi-select';
import { Field } from '~/components/promotion/editor-section';
import { serverMessage } from '~/lib/api/server-error';
import { customerGroupsApi } from '~/lib/api/client';
import { useCustomerGroupsList } from '~/hooks/use-customer-groups';
import { useIncludeExcludeLists } from '~/hooks/use-include-exclude-lists';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	customerId: number | null;
	customerName?: string;
}

const overrideKeys = (cid: number) => ['customer-group-overrides', cid] as const;

/**
 * Manual customer-group membership overrides for a single customer (force-include / force-exclude),
 * on top of each group's dynamic expression. Backed by DynamicExpressionEntityOverride via
 * CustomerGroups/CustomerOverrides + SaveCustomerOverrides. Exclusion wins, so a group cannot be in both lists.
 */
export function CustomerGroupOverridesDialog({ open, onOpenChange, customerId, customerName }: Props) {
	const { t } = useTranslation();
	const qc = useQueryClient();
	const { data: groups } = useCustomerGroupsList();
	const { include, exclude, setBoth, setIncludeList, setExcludeList } = useIncludeExcludeLists<number>(n => n);

	const { data } = useQuery({
		queryKey: overrideKeys(customerId ?? 0),
		queryFn: async () => (await customerGroupsApi.customerOverrides(customerId as number)).data,
		enabled: open && customerId != null && customerId > 0
	});

	useEffect(() => {
		setBoth(data?.include_group_ids ?? [], data?.exclude_group_ids ?? []);
	}, [data, setBoth]);

	const save = useMutation({
		mutationFn: async () =>
			(
				await customerGroupsApi.saveCustomerOverrides({
					customer_id: customerId as number,
					include_group_ids: include,
					exclude_group_ids: exclude
				})
			).data,
		onSuccess: () => {
			toast.success(t('common.save'));
			qc.invalidateQueries({ queryKey: overrideKeys(customerId ?? 0) });
			onOpenChange(false);
		},
		onError: e => toast.error(serverMessage(e))
	});

	const opts = groups ?? [];
	const byIds = (ids: number[]) => opts.filter(g => g.id != null && ids.includes(g.id));
	const toIds = (sel: typeof opts) => sel.map(g => g.id).filter((id): id is number => id != null);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>{t('customers.list.groupOverrides.title')}</DialogTitle>
					<DialogDescription>{customerName || t('customers.list.groupOverrides.subtitle')}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4">
					<Field label={t('customers.list.groupOverrides.included')}>
						<MultiSelect
							value={byIds(include)}
							onChange={next => setIncludeList(toIds(next))}
							options={opts}
							getKey={g => String(g.id)}
							getLabel={g => g.nm ?? ''}
							placeholder={t('customers.list.groupOverrides.none')}
						/>
					</Field>
					<Field label={t('customers.list.groupOverrides.excluded')}>
						<MultiSelect
							value={byIds(exclude)}
							onChange={next => setExcludeList(toIds(next))}
							options={opts}
							getKey={g => String(g.id)}
							getLabel={g => g.nm ?? ''}
							placeholder={t('customers.list.groupOverrides.none')}
						/>
					</Field>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						{t('common.cancel')}
					</Button>
					<Button onClick={() => save.mutate()} disabled={save.isPending}>
						{t('common.save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
